import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  CELL_WIDTH,
  REDUCED_SCROLL_MULTIPLIER,
  SCROLL_THRESHOLD_WEEKS,
  WEEK_WINDOW_BUFFER,
} from '../utils/gridConstants';
import { getYearFromWeek } from '../utils/weekUtils';

/**
 * Custom hook for managing infinite scroll and scroll state in the planner grid.
 * Handles:
 * - Visible window calculation for windowed rendering
 * - Infinite scroll (loading more weeks past/future)
 * - Scroll position restoration after week loading
 * - Year tracking as user scrolls
 * - Scroll navigation button state
 *
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.containerRef - Ref to the scrollable container
 * @param {Array} options.weeks - Array of week strings
 * @param {Map} options.weekIndexMap - Map of week string to column index
 * @param {string} options.currentWeekString - Current week in ISO format
 * @param {Object} options.loadedWeekRange - { fromWeek, toWeek } range of loaded weeks
 * @param {boolean} options.isLoadingMoreWeeks - Whether weeks are currently being loaded
 * @param {Function} options.onLoadMorePast - Callback to load more past weeks
 * @param {Function} options.onLoadMoreFuture - Callback to load more future weeks
 * @param {number} options.weeksToLoad - Number of weeks loaded at a time
 * @param {boolean} options.isTouchDevice - Whether on a touch device
 * @param {boolean} options.isSafari - Whether browser is Safari
 * @returns {Object} Scroll state and handlers
 */
const useInfiniteScroll = ({
  containerRef,
  weeks,
  weekIndexMap,
  currentWeekString,
  loadedWeekRange,
  isLoadingMoreWeeks,
  onLoadMorePast,
  onLoadMoreFuture,
  weeksToLoad = 12,
  isTouchDevice = false,
  isSafari = false,
}) => {
  // Scroll state refs
  const isLoadingPastRef = useRef(false);
  const isLoadingFutureRef = useRef(false);
  const scrollAnchorRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const initialScrollCompleteRef = useRef(false);
  const initialWeeksLoadedRef = useRef(false);
  const initialScrollTimeRef = useRef(null);
  const hasUserScrolledRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTickingRef = useRef(false);
  const previousScrollLeftRef = useRef(0);
  const yearTransitionTimeoutRef = useRef(null);
  const currentYearRef = useRef(getYearFromWeek(currentWeekString));

  // Batched scroll state to reduce re-renders during scroll (Safari perf optimization)
  const [scrollState, setScrollState] = useState({
    visibleWindow: { start: 0, end: -1 },
    canScrollLeft: false,
    canScrollRight: true,
  });

  const [previousYear, setPreviousYear] = useState(null);

  const { visibleWindow, canScrollLeft, canScrollRight } = scrollState;

  // Scroll threshold in pixels
  const scrollThresholdPx = useMemo(
    () => CELL_WIDTH * SCROLL_THRESHOLD_WEEKS,
    [],
  );

  // Restore scroll position after week loading
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (
      !container
      || !initialWeeksLoadedRef.current
      || !hasAutoScrolledRef.current
    ) return;

    if (scrollAnchorRef.current?.shouldRestore) {
      const { targetScrollLeft, captureAtRestore } = scrollAnchorRef.current;
      const finalScrollLeft = captureAtRestore
        ? container.scrollLeft
        : targetScrollLeft;

      container.scrollTo({
        left: finalScrollLeft,
        behavior: 'instant',
      });
      scrollAnchorRef.current = null;

      requestAnimationFrame(() => {
        container.dispatchEvent(new Event('scroll'));
      });
    }
  }, [weeks.length, containerRef]);

  // Unified scroll handler: visibility, year tracking, visible window, and infinite scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container || weeks.length === 0) return undefined;

    const updateScrollState = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;

      // === Update visible window and scroll nav buttons ===
      const start = Math.max(
        0,
        Math.floor(scrollLeft / CELL_WIDTH) - WEEK_WINDOW_BUFFER,
      );
      const end = Math.min(
        weeks.length - 1,
        Math.ceil((scrollLeft + clientWidth) / CELL_WIDTH) + WEEK_WINDOW_BUFFER,
      );

      // Batch all scroll-related state updates into single setState call
      const newCanScrollLeft = scrollLeft > 0;
      const newCanScrollRight = scrollLeft + clientWidth < scrollWidth - 1;
      setScrollState((prev) => {
        const windowChanged = prev.visibleWindow.start !== start || prev.visibleWindow.end !== end;
        const scrollChanged = prev.canScrollLeft !== newCanScrollLeft
          || prev.canScrollRight !== newCanScrollRight;
        if (!windowChanged && !scrollChanged) return prev;
        return {
          visibleWindow: windowChanged ? { start, end } : prev.visibleWindow,
          canScrollLeft: newCanScrollLeft,
          canScrollRight: newCanScrollRight,
        };
      });

      // === Current week visibility (emit to parent) ===
      if (initialScrollCompleteRef.current) {
        const currentWeekIndex = weekIndexMap.get(currentWeekString);
        if (currentWeekIndex !== undefined) {
          const currentWeekLeftEdge = currentWeekIndex * CELL_WIDTH;
          const currentWeekRightEdge = (currentWeekIndex + 1) * CELL_WIDTH;
          const isVisible = currentWeekRightEdge > scrollLeft
            && currentWeekLeftEdge < scrollLeft + clientWidth;

          window.dispatchEvent(
            new CustomEvent('planner:weekVisibilityChange', {
              detail: { isVisible },
            }),
          );
        }
      }

      // === Year tracking ===
      const middleScrollPosition = scrollLeft + clientWidth / 2;
      const middleWeekIndex = Math.floor(middleScrollPosition / CELL_WIDTH);
      const middleWeek = weeks[middleWeekIndex];

      if (middleWeek) {
        const year = getYearFromWeek(middleWeek);
        if (year !== currentYearRef.current) {
          setPreviousYear(currentYearRef.current);
          currentYearRef.current = year;
          if (yearTransitionTimeoutRef.current) {
            clearTimeout(yearTransitionTimeoutRef.current);
          }
          yearTransitionTimeoutRef.current = setTimeout(
            () => setPreviousYear(null),
            500,
          );
        }
      }

      // === Infinite scroll ===
      if (
        loadedWeekRange.fromWeek
        && loadedWeekRange.toWeek
        && initialWeeksLoadedRef.current
        && hasUserScrolledRef.current
      ) {
        // Load past weeks
        if (
          scrollLeft <= scrollThresholdPx
          && !isLoadingPastRef.current
          && !isLoadingMoreWeeks
          && onLoadMorePast
        ) {
          isLoadingPastRef.current = true;

          scrollAnchorRef.current = {
            shouldRestore: true,
            targetScrollLeft: scrollLeft + weeksToLoad * CELL_WIDTH,
          };

          onLoadMorePast(loadedWeekRange.fromWeek).finally(() => {
            isLoadingPastRef.current = false;
          });
        }

        // Load future weeks
        const scrollRight = scrollWidth - scrollLeft - clientWidth;
        if (
          scrollRight <= scrollThresholdPx
          && !isLoadingFutureRef.current
          && !isLoadingMoreWeeks
          && onLoadMoreFuture
        ) {
          isLoadingFutureRef.current = true;

          scrollAnchorRef.current = {
            shouldRestore: true,
            captureAtRestore: true,
          };

          onLoadMoreFuture(loadedWeekRange.toWeek).finally(() => {
            isLoadingFutureRef.current = false;
          });
        }
      }
    };

    const handleScroll = () => {
      // Track user scroll for infinite scroll activation
      if (!initialWeeksLoadedRef.current) return;

      const currentScrollLeft = container.scrollLeft;

      if (
        !hasUserScrolledRef.current
        && initialScrollTimeRef.current
        && Date.now() - initialScrollTimeRef.current >= 500
        && Math.abs(currentScrollLeft - previousScrollLeftRef.current) > 1
      ) {
        hasUserScrolledRef.current = true;
      }
      previousScrollLeftRef.current = currentScrollLeft;

      // RAF throttle for performance
      if (scrollTickingRef.current) return;
      scrollTickingRef.current = true;
      requestAnimationFrame(() => {
        updateScrollState();
        scrollTickingRef.current = false;
      });
    };

    // Initial state update
    updateScrollState();

    container.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (yearTransitionTimeoutRef.current) {
        clearTimeout(yearTransitionTimeoutRef.current);
      }
    };
  }, [
    containerRef,
    weeks,
    weekIndexMap,
    currentWeekString,
    loadedWeekRange.fromWeek,
    loadedWeekRange.toWeek,
    isLoadingMoreWeeks,
    onLoadMorePast,
    onLoadMoreFuture,
    weeksToLoad,
    scrollThresholdPx,
  ]);

  // Normalize horizontal wheel speed (non-touch devices only)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isTouchDevice || isSafari) return undefined;

    const handleWheel = (event) => {
      const {
        deltaMode, deltaX, deltaY, shiftKey,
      } = event;
      let modeMultiplier = 1;
      if (deltaMode === 1) {
        modeMultiplier = 16;
      } else if (deltaMode === 2) {
        modeMultiplier = container.clientWidth;
      }

      const pixelDeltaX = deltaX * modeMultiplier;
      const pixelDeltaY = deltaY * modeMultiplier;
      const shiftOnly = pixelDeltaX === 0 && shiftKey;
      const horizontalIntent = shiftOnly || Math.abs(pixelDeltaX) > Math.abs(pixelDeltaY);

      if (!horizontalIntent) return;

      const appliedDelta = shiftOnly ? pixelDeltaY : pixelDeltaX;
      if (appliedDelta === 0) return;

      event.preventDefault();
      container.scrollLeft += appliedDelta * REDUCED_SCROLL_MULTIPLIER;
    };

    container.addEventListener('wheel', handleWheel, {
      passive: false,
    });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [containerRef, isTouchDevice, isSafari]);

  // Auto-scroll to current week on initial load
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || weeks.length === 0 || hasAutoScrolledRef.current) {
      return;
    }

    const currentWeekIndex = weekIndexMap.get(currentWeekString);
    if (currentWeekIndex === undefined) return;

    hasAutoScrolledRef.current = true;
    scrollAnchorRef.current = null;
    initialWeeksLoadedRef.current = true;
    initialScrollTimeRef.current = Date.now();

    const targetScrollLeft = currentWeekIndex * CELL_WIDTH;
    isProgrammaticScrollRef.current = true;

    // Single RAF + setTimeout for Safari compatibility
    requestAnimationFrame(() => {
      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: 'instant',
      });

      // Use setTimeout instead of nested RAFs - gives browser time to settle layout
      setTimeout(() => {
        initialScrollCompleteRef.current = true;
        isProgrammaticScrollRef.current = false;
        container.dispatchEvent(new Event('scroll'));
      }, 16); // ~1 frame
    });
  }, [containerRef, weeks.length, weekIndexMap, currentWeekString]);

  /**
   * Jump to current week (today button handler)
   */
  const jumpToToday = useCallback(() => {
    const container = containerRef.current;
    if (!container || weeks.length === 0) return;

    const currentWeekIndex = weekIndexMap.get(currentWeekString);
    if (currentWeekIndex === undefined) return;

    hasUserScrolledRef.current = true;
    isProgrammaticScrollRef.current = true;

    container.scrollTo({
      left: Math.max(0, currentWeekIndex * CELL_WIDTH),
      behavior: 'smooth',
    });

    // Allow visibility check after scroll animation completes
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      // Force an immediate visibility update
      if (container) {
        const event = new Event('scroll', {
          bubbles: true,
          cancelable: true,
        });
        container.dispatchEvent(event);
      }
    }, 700);
  }, [containerRef, weeks.length, weekIndexMap, currentWeekString]);

  // Scroll to a specific week
  // Used for bulk copy when pasting into current calendar
  // to ensure the newly pasted blocks are visible immediately
  const jumpToTargetWeek = useCallback((weekString) => {
    const container = containerRef.current;
    if (!container || weeks.length === 0) return;

    const weekIndex = weekIndexMap.get(weekString);
    if (weekIndex === undefined) return;

    hasUserScrolledRef.current = true;
    isProgrammaticScrollRef.current = true;

    container.scrollTo({
      left: Math.max(0, weekIndex * CELL_WIDTH),
      behavior: 'smooth',
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      if (container) {
        const event = new Event('scroll', { bubbles: true, cancelable: true });
        container.dispatchEvent(event);
      }
    }, 700);
  }, [containerRef, weeks.length, weekIndexMap]);

  /**
   * Scroll navigation with hold-to-scroll support
   */
  const scrollAnimationRef = useRef(null);
  const SCROLL_SPEED = 12; // Pixels per frame

  const startScrolling = useCallback(
    (direction) => {
      const container = containerRef.current;
      if (!container) return;
      hasUserScrolledRef.current = true;

      const scroll = () => {
        const { current: scrollingContainer } = containerRef;
        if (!scrollingContainer) {
          scrollAnimationRef.current = null;
          return;
        }

        const delta = direction === 'left' ? -SCROLL_SPEED : SCROLL_SPEED;
        // Update scroll position on the resolved container reference
        scrollingContainer.scrollLeft += delta;

        // Continue animation
        scrollAnimationRef.current = requestAnimationFrame(scroll);
      };

      // Start the animation loop
      scrollAnimationRef.current = requestAnimationFrame(scroll);
    },
    [containerRef],
  );

  const stopScrolling = useCallback(() => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  // Clean up animation on unmount
  useEffect(
    () => () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    },
    [],
  );

  return {
    // Visible window for windowed rendering
    visibleWindow,

    // Scroll navigation button state
    canScrollLeft,
    canScrollRight,

    // Year tracking (for header display)
    previousYear,
    currentYear: currentYearRef.current,

    // Scroll navigation handlers
    startScrolling,
    stopScrolling,
    jumpToToday,
    jumpToTargetWeek,

    // Refs for external access if needed
    hasUserScrolledRef,
    isProgrammaticScrollRef,
    initialScrollCompleteRef,
  };
};

export default useInfiniteScroll;
